/**
 * Toucan Music: connecting this browser to Spotify. After asking for the
 * client id once, it starts the PKCE login and finishes it when Spotify
 * redirects back.
 * The client id dialog is appended to the body when needed, like the
 * platform's `requestLogin` dialog.
 */

import { render } from "preact";
import { useRef } from "preact/hooks";
import {
  describeError,
  required,
  showError,
  ToucanButton,
  ToucanDialogActions,
} from "toucan";

import {
  beginLogin,
  clientId,
  completeLogin,
  logout,
  readCallback,
  redirectUri,
  setClientId,
} from "./spotify.ts";
import { disconnectPlayer } from "./player.ts";

/**
 * The client id dialog's contents, which say what to register and hold an
 * uncontrolled field for the id that is read when Save is pressed.
 */
function ClientId({ finish }: { finish: (id: string | null) => void }) {
  const field = useRef<HTMLInputElement>(null);
  return (
    <article>
      <h2>Connect Spotify</h2>
      <p>
        Create an app at{" "}
        <a
          href="https://developer.spotify.com/dashboard"
          target="_blank"
          rel="noreferrer"
        >
          developer.spotify.com/dashboard
        </a>
        , tick <strong>Web API</strong> and <strong>Web Playback SDK</strong>,
        and register this exact redirect URI:
      </p>
      <p>
        <code>{redirectUri()}</code>
      </p>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          const value = required(field.current, "client id").value.trim();
          // A blank id keeps the dialog open.
          if (value === "") {
            return;
          }
          setClientId(value);
          finish(value);
        }}
      >
        <label>
          Client ID{" "}
          <input
            ref={field}
            name="clientId"
            required
            autocomplete="off"
            defaultValue={clientId() ?? ""}
          />
        </label>
        <ToucanDialogActions>
          <ToucanButton
            onClick={() => {
              finish(null);
            }}
          >
            Cancel
          </ToucanButton>
          <ToucanButton submit variant="primary">
            Save
          </ToucanButton>
        </ToucanDialogActions>
      </form>
    </article>
  );
}

/** Ask for the Spotify client id, explaining exactly what to register. */
export async function askClientId(): Promise<string | null> {
  const dialog = document.createElement("dialog");
  dialog.id = "client-id-dialog";
  dialog.className = "mobile-fullscreen";
  document.body.append(dialog);
  return await new Promise<string | null>((resolve) => {
    const finish = (value: string | null): void => {
      render(null, dialog);
      dialog.close();
      dialog.remove();
      resolve(value);
    };
    dialog.addEventListener("cancel", () => {
      finish(null);
    });
    render(<ClientId finish={finish} />, dialog);
    dialog.showModal();
  });
}

/**
 * Send a browser on `localhost` to the same page on 127.0.0.1, and answer
 * whether it is going. Spotify refuses `localhost` as a redirect URI, and the
 * two are different origins, so the app runs only on the loopback address.
 */
export function moveToLoopback(): boolean {
  if (location.hostname !== "localhost") {
    return false;
  }
  const there = new URL(location.href);
  there.hostname = "127.0.0.1";
  location.replace(there.href);
  return true;
}

/**
 * Start the login, asking for the client id first if this browser has none.
 * `goThere` navigates to Spotify, passed in so a test can press the button
 * without leaving the page.
 */
export async function connectSpotify(
  goThere: (url: string) => void,
): Promise<void> {
  const id = clientId() ?? (await askClientId());
  if (id === null) {
    return;
  }
  await beginLogin(id, goThere);
}

/** Forget the tokens and drop the player. */
export function disconnectSpotify(): void {
  logout();
  disconnectPlayer();
}

/**
 * Handle the redirect back from Spotify, if this load is one. The code is
 * single-use, so the query string is stripped either way, or a reload would
 * replay it. A refused or failed login keeps any tokens the browser already
 * had. Awaited before the first render, so the app draws with the tokens this
 * load ends up holding.
 */
export async function handleRedirect(): Promise<void> {
  const callback = readCallback(location.search);
  if (callback.kind === "none") {
    return;
  }
  history.replaceState(null, "", `${location.pathname}${location.hash}`);
  if (callback.kind === "error") {
    showError(`Spotify refused the login: ${callback.message}`);
    return;
  }
  const id = clientId();
  if (id === null) {
    showError("Spotify sent you back, but this browser has no client id.");
    return;
  }
  try {
    await completeLogin(callback.code, callback.state, id);
  } catch (error) {
    showError(
      `Could not finish connecting to Spotify: ${describeError(error)}`,
    );
  }
}
